import { serializeSyncDiagnosticException, type SyncDiagnosticException } from './syncDiagnosticException';
import { getSQLiteOperationFailureContext, type SQLiteOperationDiagnostic } from './SQLiteOperationDiagnostics';

export interface SyncDiagnosticResult {
  companyId: string;
  userId: string;
  traceId: string;
  startedAt: number;
  success: boolean;
  error: string;
  exception?: SyncDiagnosticException;
  networkConnected?: boolean;
  phase?: 'failure' | 'complete';
  stage?: string;
  sqliteOperations?: SQLiteOperationDiagnostic[];
}

const listeners = new Set<(result: SyncDiagnosticResult) => void>();
export const registerSyncDiagnosticListener = (listener: (result: SyncDiagnosticResult) => void): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

/** Observação síncrona preserva o buffer antes de outro ciclo; nunca altera o resultado do sync. */
export const reportSyncDiagnosticResult = (result: SyncDiagnosticResult): void => {
  listeners.forEach(listener => {
    try { listener(activeCycle ? { ...result, ...activeCycle,
      ...(activeSQLiteFailures.length ? { sqliteOperations: getActiveSyncSQLiteFailures() } : {}) } : result); } catch { /* Diagnóstico não pode falhar o sincronismo. */ }
  });
};

let activeCycle: Omit<SyncDiagnosticResult, 'success' | 'error'> | null = null;
let activeSQLiteFailures: SQLiteOperationDiagnostic[] = [];
export const getActiveSyncSQLiteFailures = (): SQLiteOperationDiagnostic[] => [...activeSQLiteFailures];
export const beginSyncDiagnosticCycle = (cycle: NonNullable<typeof activeCycle>): (() => void) => {
  activeCycle = cycle;
  activeSQLiteFailures = [];
  return () => { if (activeCycle === cycle) { activeCycle = null; activeSQLiteFailures = []; } };
};
/** Somente após esgotar o tratamento local; colisão recuperada não passa por aqui. */
export const reportUnrecoveredSyncFailure = (error: unknown, stage: string, networkConnected?: boolean): void => {
  if (!activeCycle) return;
  try {
    const sqliteOperations = getSQLiteOperationFailureContext(error);
    for (const operation of sqliteOperations) {
      if (!activeSQLiteFailures.some(item => item.operationId === operation.operationId)) {
        activeSQLiteFailures = [...activeSQLiteFailures, operation].slice(-8);
      }
    }
    const exception = serializeSyncDiagnosticException(error);
    const message = exception.message ?? exception.code ?? 'sync_failed';
    reportSyncDiagnosticResult({ ...activeCycle, success: false, error: message, exception, networkConnected, phase: 'failure', stage,
      ...(activeSQLiteFailures.length ? { sqliteOperations: getActiveSyncSQLiteFailures() } : {}) });
  } catch { /* A observação não pode alterar a falha original. */ }
};

export const getActiveSyncTraceId = (): string | undefined => activeCycle?.traceId;
